<template>
  <div v-if="!cisionLanguage && isInStoryblokAdmin()" class="p-16 bg-bg-warning text-text-warning type-sm">
    Cision language not configured for this domain
  </div>
  <div
    v-else-if="cisionLanguage"
    v-editable="blok"
    class="CisionTeaserBlock layout-container"
    :class="[desktopWidth('md'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >

    <div class="flex flex-col gap-8 desk:flex-row desk:gap-24 justify-between items-start">
      <div class="desk:basis-1/2">
        <component
          :is="blok.headlineTag || 'div'"
          class="type-headline-3xl"
        >
          {{ blok.headline }}
        </component>
        <div class="my-24 wysiwyg">
          <ExtendedWysiwyg :content="blok.content"/>
        </div>

        <NuxtLink
          v-if="$sbLinkCheck(blok.cisionPage)"
          :to="$sbLinkFix(blok.cisionPage)"
          class="btn btn--secondary"
        >
          {{ blok.buttonText}}
        </NuxtLink>

      </div>

      <div class="desk:basis-1/2">
        <div
          v-for="item in displayItems"
          :key="item.encryptedId"
          class="cursor-pointer border-b border-border-base pb-24 mb-24 last:border-0 last:mb-0 last:pb-0"
          @click="goToRelease(item.encryptedId)"
        >
          <div class="mb-4">
            <span class="type-headline-sm">{{ item.date }}</span>
            <span class="type-sm">{{ item.isRegulatory ? $t('cision.regulatory') : '' }}</span>
          </div>
          <div class="type-headline-md mb-6">{{ item.title }}</div>
          <div v-if="item.summary">{{ item.summary }}</div>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import type { DatasourceHeadlineTypes, StoryBlokLink, StoryBlokPropBase } from '#constants/types/storyblok';
import useDesktopWidth from '~/composables/useVisualAppearancePlugin';
import type { ISbRichtext } from 'storyblok-js-client';
import type { CisionFeedResponse, CisionReleaseSummary } from '#constants/types/cision';
import { useGlobalContentStore } from '~/store/globalContent';
import useStoryblokAdmin from '~/composables/useStoryblokAdmin';

export type CisionTeaserBlockType = StoryBlokPropBase & {
  headline: string,
  headlineTag?: DatasourceHeadlineTypes,
  content: ISbRichtext,
  perPage: string, // number but as string
  cisionPage: StoryBlokLink,
  buttonText: string,
}

const props = defineProps<{
  blok: CisionTeaserBlockType,
  exclude?: string, // exclude release with this id
}>();

const { desktopWidth, blockPaddingBottom, blockPaddingTop } = useDesktopWidth(props);

const globalContent = useGlobalContentStore();
const cisionLanguage = globalContent.domainMap?.features?.cisionLanguage;
const router = useRouter();
const { $sbLinkFix } = useNuxtApp();
const { isInStoryblokAdmin } = useStoryblokAdmin();

type DisplayItem = {
  encryptedId: string;
  date: string;
  isRegulatory: boolean;
  title: string;
  summary: string;
};

const displayItems = ref<DisplayItem[]>([]);

if (cisionLanguage) {
  const perPage = Number(props.blok.perPage);
  const fetchSize = props.exclude ? perPage + 1 : perPage;

  const { data } = await useAsyncData('cision-teaser', async() => {
    const feed = await $fetch<CisionFeedResponse>('/api/cision/releases', {
      query: { pageIndex: 0, pageSize: fetchSize, language: cisionLanguage },
    });

    return feed.Releases.map((release: CisionReleaseSummary): DisplayItem => ({
      encryptedId: release.EncryptedId,
      date: release.PublishDate.slice(0, 10),
      isRegulatory: release.IsRegulatory,
      title: release.Title,
      summary: release.Intro,
    }));
  });

  if (data.value) {
    displayItems.value = props.exclude
      ? data.value.filter((item) => item.encryptedId !== props.exclude).slice(0, perPage)
      : data.value;
  }
}

const goToRelease = (encryptedId: string) => {
  const url = String($sbLinkFix(props.blok.cisionPage));
  const isExternal = (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//'));
  if (isExternal) {
    window.location.href = `${url}?show=${encodeURIComponent(encryptedId)}`;
  } else {
    router.push({ path: url, query: { show: encryptedId } });
  }
};
</script>

<style scoped lang="postcss">
</style>
